<template>
  <div class="server-sparkline">
    <svg v-if="points.length > 1" viewBox="0 0 220 46" preserveAspectRatio="none">
      <line x1="0" y1="42" x2="220" y2="42" stroke="var(--theme-border)" stroke-width="1"/>
      <path :d="areaPath" fill="url(#server-sparkline-fill)" stroke="none"/>
      <path :d="linePath" fill="none" stroke="var(--theme-accent-primary)" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
      <circle :cx="lastPoint.x" :cy="lastPoint.y" r="3" fill="var(--theme-accent-primary)"/>
      <defs>
        <linearGradient id="server-sparkline-fill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="var(--theme-accent-primary)" stop-opacity=".28"/>
          <stop offset="100%" stop-color="var(--theme-accent-primary)" stop-opacity="0"/>
        </linearGradient>
      </defs>
    </svg>
    <div v-else class="server-sparkline__empty">
      {{ emptyLabel }}
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

// Inline-SVG sparkline, no charting library — matches `server-detail.html`'s
// spark-stack exactly (single copper accent line + gradient fill, no
// per-metric colour, no legend/axis). The estate has no shared chart
// component to reuse yet (checked `icarus/node_modules/@apexl` — only
// `icarus-core`, no chart package installed anywhere in this project or
// Town Centre's `icarus/package.json`), so a bespoke chart-lib dependency
// would be new estate surface area for a ~10-point trend line; inline SVG is
// both what the design shows and the lower-footprint choice.
const props = defineProps({
  // [{ts, value}] ascending by ts, per MetricsController's `/metrics` shape.
  series: {
    type: Array,
    default: () => ([]),
  },
  emptyLabel: {
    type: String,
    default: 'No data yet',
  },
});

const points = computed(() => {
  const series = props.series || [];
  if (series.length < 2) {
    return [];
  }

  const values = series.map((s) => Number(s.value));
  const min = Math.min(...values);
  const max = Math.max(...values);
  const range = max - min || 1;

  const width = 220;
  const height = 46;
  const top = 4;
  const bottom = 42;

  return series.map((s, i) => {
    const x = (i / (series.length - 1)) * width;
    const normalised = (Number(s.value) - min) / range;
    const y = bottom - normalised * (bottom - top);
    return { x: Math.round(x * 100) / 100, y: Math.round(y * 100) / 100 };
  });
});

const lastPoint = computed(() => points.value[points.value.length - 1] || { x: 0, y: 42 });

const linePath = computed(() => points.value.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x},${p.y}`).join(' '));

const areaPath = computed(() => {
  if (!points.value.length) {
    return '';
  }
  const first = points.value[0];
  const last = points.value[points.value.length - 1];
  return `${linePath.value} L${last.x},42 L${first.x},42 Z`;
});
</script>

<style lang="scss">
.server-sparkline {
  svg {
    width: 100%;
    height: auto;
    display: block;
  }

  &__empty {
    height: 46px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 11px;
    color: var(--theme-text-tertiary);
    border: 1.5px dashed var(--theme-border-strong);
    border-radius: var(--theme-radius, 6px);
  }
}
</style>
